import React from 'react';
import { Hit } from '@algolia/client-search';
import { MongooseModel, PageInfo } from '../models';
import { useAlgolia } from './useAlgolia';

interface UseAlgoliaSearchProps {
  restrictSearchableAttributes?: string[];
  hitsPerPage?: number;
  index: string;
  filters?: string;
  pageInfo: Partial<PageInfo>;
  setPageInfo: React.Dispatch<React.SetStateAction<Partial<PageInfo>>>;
}

type TUseAlgoliaSearch<T> = {
  searchResult: Hit<T>[];
  loading: boolean;
  fetchData: (props: FetchDataProps) => Promise<TFetchData<T>>;
};

export type FetchDataProps = {
  searchText?: string;
  newPage?: number;
  filters?: string;
};

export type TFetchData<T> = {
  pageInfo: PageInfo;
  items: T[];
};

export function useAlgoliaSearch<T extends MongooseModel>({
  restrictSearchableAttributes = [],
  pageInfo,
  setPageInfo,
  index,
  filters = '',
}: UseAlgoliaSearchProps): TUseAlgoliaSearch<T> {
  const { searchClient } = useAlgolia();
  const searchIndex = searchClient.initIndex(index);
  const [loading, setLoading] = React.useState(true);
  const [searchResult, setSearchResult] = React.useState<Hit<T>[]>(null);

  const fetchData = async ({
    newPage,
    searchText,
    filters: _filter,
  }: FetchDataProps) => {
    setLoading(false);
    try {
      setLoading(true);
      const {
        hits,
        page: algoliaPage,
        nbHits,
        nbPages,
      } = await searchIndex.search(searchText ?? '', {
        page: newPage,
        hitsPerPage: pageInfo.perPage,
        restrictSearchableAttributes,
        filters: _filter || filters,
      });

      if (hits?.length === 0) {
        setSearchResult(null);
        return { items: [], pageInfo: {} };
      }

      setSearchResult(hits as Hit<T>[]);

      const newPageInfo = {
        currentPage: algoliaPage,
        perPage: pageInfo.perPage,
        itemCount: nbHits,
        hasNextPage: algoliaPage < nbPages - 1,
        hasPreviousPage: algoliaPage > 0,
        pageCount: nbPages,
      };

      setPageInfo(newPageInfo);

      return { items: hits, pageInfo: newPageInfo };
    } catch (error) {
      console.log(error);

      return { items: [], pageInfo: {} };
    } finally {
      setLoading(false);
    }
  };

  return {
    searchResult,
    loading,
    fetchData,
  } as TUseAlgoliaSearch<T>;
}
